import { getInvoicePreview } from '@/modules/Invoice/services'
import { ModalProps } from '@/types/shared'
import { Dialog, Button } from 'vnetwork-ui'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { useRef, useState } from 'react'
import DownLoadIcons from '@/icons/DownLoadIcons'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface PreviewInvoiceModalProps extends ModalProps {
    type: 'renew' | 'statement'
    invoiceID: number
}

const PreviewInvoice: React.FC<PreviewInvoiceModalProps> = ({
    type,
    invoiceID,
    onClose,
    open,
}) => {
    const { t } = useTranslation()
    const invoiceRef = useRef<HTMLDivElement>(null)
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICE_PREVIEW, type, invoiceID],
        queryFn: () => getInvoicePreview({ invoiceID: invoiceID, type }),
    })

    const handleDownloadPDF = async () => {
        if (!invoiceRef.current || isGeneratingPDF) return

        setIsGeneratingPDF(true)
        try {
            const canvas = await html2canvas(invoiceRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                removeContainer: true,
                imageTimeout: 15000,
                logging: false,
                width: invoiceRef.current.scrollWidth,
                height: invoiceRef.current.scrollHeight,
            })

            const imgData = canvas.toDataURL('image/jpeg', 0.92)
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            })

            const imgWidth = 210
            const pageHeight = 295
            const imgHeight = (canvas.height * imgWidth) / canvas.width
            let heightLeft = imgHeight
            let position = 0

            if (imgHeight <= pageHeight) {
                pdf.addImage(
                    imgData,
                    'JPEG',
                    0,
                    0,
                    imgWidth,
                    imgHeight,
                    '',
                    'FAST'
                )
            } else {
                pdf.addImage(
                    imgData,
                    'JPEG',
                    0,
                    position,
                    imgWidth,
                    imgHeight,
                    '',
                    'FAST'
                )
                heightLeft -= pageHeight

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight
                    pdf.addPage()
                    pdf.addImage(
                        imgData,
                        'JPEG',
                        0,
                        position,
                        imgWidth,
                        imgHeight,
                        '',
                        'FAST'
                    )
                    heightLeft -= pageHeight
                }
            }

            pdf.save(`invoice-${type}-${invoiceID}.pdf`)
        } catch (error) {
            console.error('Error generating PDF:', error)
        } finally {
            setIsGeneratingPDF(false)
        }
    }

    const stateComponent = renderState()

    return (
        <Dialog onClose={onClose} open={open} maxWidth="lg">
            {stateComponent ? (
                stateComponent
            ) : data ? (
                <div>
                    <div className="flex justify-end mb-4">
                        <Button
                            onClick={handleDownloadPDF}
                            disabled={isGeneratingPDF}
                            loading={isGeneratingPDF}
                            startIcon={!isGeneratingPDF && <DownLoadIcons />}
                        >
                            {isGeneratingPDF
                                ? t('generating-pdf')
                                : t('download-pdf')}
                        </Button>
                    </div>
                    <div
                        ref={invoiceRef}
                        dangerouslySetInnerHTML={{ __html: data }}
                    />
                </div>
            ) : null}
        </Dialog>
    )
}

export default PreviewInvoice
